import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { showToast as globalShowToast } from '@/utils/toast'

// 应用状态类型定义
export interface AppState {
  isLoading: boolean
  isOnline: boolean
  isPageVisible: boolean
  theme: 'light' | 'dark' | 'auto'
  language: string
  deviceInfo: DeviceInfo
}

export interface DeviceInfo {
  userAgent: string
  isMobile: boolean
  isIOS: boolean
  isAndroid: boolean
  isWeChat: boolean
  isAlipay: boolean
  screenWidth: number
  screenHeight: number
  safeArea: {
    top: number
    bottom: number
    left: number
    right: number
  }
}

export const useAppStore = defineStore('app', () => {
  // 应用状态
  const isLoading = ref(false)
  const isOnline = ref(navigator.onLine)
  const isPageVisible = ref(!document.hidden)
  const theme = ref<'light' | 'dark' | 'auto'>('light')
  const language = ref('zh-CN')

  // 设备信息
  const deviceInfo = ref<DeviceInfo>({
    userAgent: navigator.userAgent,
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ),
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isAndroid: /Android/.test(navigator.userAgent),
    isWeChat: /MicroMessenger/i.test(navigator.userAgent),
    isAlipay: /AlipayClient/i.test(navigator.userAgent),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    safeArea: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    }
  })

  // 计算属性
  const isDarkMode = computed(() => {
    if (theme.value === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value === 'dark'
  })

  const isInApp = computed(() => {
    return deviceInfo.value.isWeChat || deviceInfo.value.isAlipay
  })

  // 初始化应用
  const init = () => {
    // 获取保存的设置
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'auto'
    const savedLanguage = localStorage.getItem('language')

    if (savedTheme) {
      theme.value = savedTheme
    }

    if (savedLanguage) {
      language.value = savedLanguage
    }

    // 更新设备信息
    updateDeviceInfo()

    // 监听窗口大小变化
    window.addEventListener('resize', updateDeviceInfo)

    // 监听主题变化
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', updateTheme)

    // 初始设置主题
    updateTheme()
  }

  // 更新设备信息
  const updateDeviceInfo = () => {
    deviceInfo.value.screenWidth = window.innerWidth
    deviceInfo.value.screenHeight = window.innerHeight

    // 获取安全区域信息
    const style = getComputedStyle(document.documentElement)
    deviceInfo.value.safeArea = {
      top: parseInt(style.getPropertyValue('--sat') || '0'),
      bottom: parseInt(style.getPropertyValue('--sab') || '0'),
      left: parseInt(style.getPropertyValue('--sal') || '0'),
      right: parseInt(style.getPropertyValue('--sar') || '0')
    }
  }

  // 更新主题
  const updateTheme = () => {
    const isDark = isDarkMode.value
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')

    // 更新状态栏颜色
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#1a1a1a' : '#007aff')
    }
  }

  // 设置加载状态
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  // 设置网络状态
  const setNetworkStatus = (online: boolean) => {
    isOnline.value = online
  }

  // 设置页面可见性
  const setPageVisibility = (visible: boolean) => {
    isPageVisible.value = visible
  }

  // 设置主题
  const setTheme = (newTheme: 'light' | 'dark' | 'auto') => {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)
    updateTheme()
  }

  // 设置语言
  const setLanguage = (newLanguage: string) => {
    language.value = newLanguage
    localStorage.setItem('language', newLanguage)
  }

  // 显示全局加载
  const showLoading = (text?: string) => {
    // 这里可以集成 Toast 或其他全局加载组件
    setLoading(true)
  }

  // 隐藏全局加载
  const hideLoading = () => {
    setLoading(false)
  }

  // 显示Toast消息
  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    globalShowToast(message, { type })
  }

  return {
    // 状态
    isLoading,
    isOnline,
    isPageVisible,
    theme,
    language,
    deviceInfo,

    // 计算属性
    isDarkMode,
    isInApp,

    // 方法
    init,
    updateDeviceInfo,
    updateTheme,
    setLoading,
    setNetworkStatus,
    setPageVisibility,
    setTheme,
    setLanguage,
    showLoading,
    hideLoading,
    showToast
  }
})
